import urlJoin from 'url-join'
import { formatCurrency } from '@exodus/formatting/lib/currency'
import { formatPrice } from '@exodus/formatting/lib/price'
import { units } from '@exodus/fiat-currencies'
import { keyBy } from '@exodus/basic-utils'
import { ASSET_TYPES, FILTER_LIST_MODES, ORDER_TYPES, PROVIDERS, SIDES } from './constants.js'
import moonpayAdapter from './moonpay/adapter.js'
import rampAdapter from './ramp/adapter.js'

const fiatCurrenciesBySymbol = keyBy(Object.values(units), (item) => item.symbol)
const providerAdapters = {
  [PROVIDERS.moonpay]: moonpayAdapter,
  [PROVIDERS.ramp]: rampAdapter,
}

const removeTrailingSlash = (path) => path.replace(/\/$/u, '')

export const getSideFrom = (side) => side === SIDES.from

export const getSideTo = (side) => side === SIDES.to

export const omitUndefinedValues = (object) =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined))

export const joinUrl = (url, ...segments) => {
  if (!url) {
    throw new Error('`url` is required')
  }

  const [urlWithoutQuery, query] = `${new URL(url)}`.split('?')
  const paths = []
  const queries = [query]

  for (const segment of segments) {
    if (typeof segment === 'object') {
      queries.push(`${new URLSearchParams(segment)}`)
      continue
    }

    const [segmentPath, ...segmentQuery] = (segment || '').split('?')

    paths.push(segmentPath)
    queries.push(segmentQuery.join('?'))
  }

  const searchParameters = `${new URLSearchParams(queries.join('&'))}`
  const search = searchParameters ? `?${searchParameters}` : ''

  return `${removeTrailingSlash(urlJoin(urlWithoutQuery, ...paths))}${search}`
}

export const uniqueArray = (array) => [...new Set(array)]

export const sortQuotes = (quotes, { preprocess = (quote) => quote } = {}) =>
  quotes.sort((a, b) => {
    const left = preprocess(a)
    const right = preprocess(b)

    if (left.type === ORDER_TYPES.buy && right.type === ORDER_TYPES.sell) {
      return -1
    }

    if (left.type === ORDER_TYPES.sell && right.type === ORDER_TYPES.buy) {
      return 1
    }

    return right.to?.amount - left.to?.amount
  })

export const formatAmount = (
  amount = 0,
  asset,
  { maxDecimals, maxSignificant = 6, useGrouping } = {}
) => {
  if (asset.type === ASSET_TYPES.fiat) {
    return formatPrice(amount, {
      useGrouping: typeof useGrouping === 'boolean' ? useGrouping : true,
      currency: fiatCurrenciesBySymbol[asset.symbol]?.name || units[asset.symbol]?.name,
    })
  }

  const [integer] = amount.toString().split('.')
  const maxDecimals_ =
    typeof maxDecimals === 'number' ? maxDecimals : asset.currency.defaultUnit.power

  const decimals = maxDecimals_ > maxSignificant ? maxSignificant : (maxDecimals_ ?? 0)
  const roundedToDecimal = formatCurrency(amount, {
    maxFraction: decimals,
    minFraction: 0,
    useGrouping: false,
  })

  return formatCurrency(roundedToDecimal, {
    format: '%v %s',
    maxSignificant: Math.max(maxSignificant, integer.length),
    symbol: asset.displayTicker,
    useGrouping,
  })
}

export const getAssetsByPaymentMethod = (assets, { client, country, paymentMethod, orderType }) => {
  const newAssets = []

  for (const asset of assets) {
    const providers = Object.entries(asset.providers).filter(([name]) =>
      client.providers.get(name).getPaymentMethod(paymentMethod, {
        country,
        currency: asset.type === ASSET_TYPES.fiat ? asset.name : undefined,
        orderType,
      })
    )

    if (providers.length > 0) {
      newAssets.push({ ...asset, providers: Object.fromEntries(providers) })
    }
  }

  return newAssets
}

export const getProviderAsset = (asset, provider, assets) => {
  const adapter = providerAdapters[provider]

  if (!adapter) {
    throw new Error('Could not find provider adapter')
  }

  return adapter.fromProviderToExodusAsset(asset, assets)
}

export const getUrlWithOrderId = (orderId, url, ...paths) => {
  if (!url) {
    return
  }

  return joinUrl(url, ...paths, omitUndefinedValues({ orderId }))
}

export const getProviderEnabled = ({ enabled = true, kyc = {}, provider, type }) => {
  if (!type) {
    return typeof enabled === 'boolean' ? enabled : Object.values(enabled || {}).includes(true)
  }

  if (typeof enabled === 'boolean') {
    return enabled
  }

  const value = typeof enabled === 'string' ? enabled : enabled[type]
  const values = {
    existing: kyc[provider],
    new: !kyc[provider],
  }

  if (value === undefined) {
    return true
  }

  return typeof value === 'boolean' ? value : values[value]
}

export const filterByCountries = (
  list = {},
  key = [],
  { mode = FILTER_LIST_MODES.blacklist, properties = [] } = {}
) => {
  const blacklist = FILTER_LIST_MODES.blacklist
  const keys = Array.isArray(key) ? key : [key]

  let graph = list

  for (const key of keys) {
    const node = graph[key]

    if (node === undefined) {
      break
    }

    if (node === true) {
      return false
    }

    if (node === false) {
      return true
    }

    if (
      typeof node === 'object' &&
      node !== null &&
      properties.some((property) => node.hasOwnProperty(property))
    ) {
      return properties.every((property) =>
        mode === blacklist ? node[property] === false : !node[property] || node[property] === false
      )
    }

    graph = node
  }

  return mode !== blacklist
}

export const buildQueryParamsWithProviders = ({ providers, ...parameters }) => {
  const query = new URLSearchParams(parameters)
  for (const provider of providers) {
    query.append('provider', provider)
  }

  return query
}

export const roundUpToPrecision = (number, precision = 6) => {
  const multiplier = 10 ** precision

  return Math.ceil(Number(number) * multiplier) / multiplier
}
